import { isDefined, isNotDefined, roundToDecimalPlaces } from "@utils/general";

import { ILineItemValues, roundVatToDecimalPlaces } from "./Document.utils";

type FieldKind = "unit-price" | "amount";

type ResolveRossumValueOptions = {
    frozen: boolean;
    relatedValue: number | null | undefined;
    derived: number | null;
    qty: number;
    rounder: (v: number) => number;
    targetFieldKind: FieldKind;
};

type RossumRecalculateFieldsConfig = {
    round: (v: number) => number;
    roundVat: (v: number) => number;
    sourceFieldKind: FieldKind;
    vatRate: number;
};

type AmountTriplet = { total: number | null; net: number | null; vat: number | null };

type RecalculateRossumItemStrategy =  FieldKind | 'zero-triplet' | null

const NULL_TRIPLET: AmountTriplet = { total: null, net: null, vat: null };

const rossumPresent = (v: number | null | undefined): boolean => isDefined(v) && v !== 0;

const isZeroTriplet = (total: number | null | undefined, net: number | null | undefined, vat: number | null | undefined): boolean =>
    total === 0 && net === 0 && vat === 0;

const deriveVatRateFromTriplet = (triplet: AmountTriplet, defaultVatRate = 0): number => {
    if (isZeroTriplet(triplet.total, triplet.net, triplet.vat)) {
        return 0;
    }

    if (isDefined(triplet.total) && isDefined(triplet.net) && triplet.net !== 0) {
        return roundToDecimalPlaces(2, ((triplet.total / triplet.net) - 1) * 100);
    }

    if (isDefined(triplet.total) && isDefined(triplet.vat)) {
        const derivedNet = triplet.total - triplet.vat;
        if (derivedNet !== 0) {
            return roundToDecimalPlaces(2, (triplet.vat / derivedNet) * 100);
        }
    }

    if (isDefined(triplet.net) && isDefined(triplet.vat) && triplet.net !== 0) {
        return roundToDecimalPlaces(2, (triplet.vat / triplet.net) * 100);
    }

    return defaultVatRate;
};

export const selectRecalculateRossumItemStrategy = (item: ILineItemValues, defaultVatRate = 0): RecalculateRossumItemStrategy => {
    const effectiveVatRate = rossumPresent(item.Vat) ? item.Vat : defaultVatRate;
    const isUnitPriceZeroTriplet = isZeroTriplet(item.TransactionUnitPrice, item.TransactionUnitPriceNet, item.TransactionUnitPriceVat);
    const isAmountZeroTriplet = isZeroTriplet(item.TransactionAmount, item.TransactionAmountNet, item.TransactionAmountVat);
    
    if(isUnitPriceZeroTriplet || isAmountZeroTriplet) {
        return 'zero-triplet';
    }
    const hasAnySeedAmount =
        rossumPresent(item.TransactionAmount) ||
        rossumPresent(item.TransactionAmountNet) ||
        (rossumPresent(item.TransactionAmountVat) && effectiveVatRate > 0);
    const hasUnitPriceSeed =
        rossumPresent(item.TransactionUnitPriceNet) ||
        rossumPresent(item.TransactionUnitPrice) ||
        (rossumPresent(item.TransactionUnitPriceVat) && effectiveVatRate > 0);
    return hasUnitPriceSeed ? "unit-price" : hasAnySeedAmount ? "amount" : null;
};

function deriveTriplet(
    { total, net, vat }: AmountTriplet,
    vatRate: number,
    round: (v: number) => number,
    roundVat: (v: number) => number
): AmountTriplet {
    if (isDefined(net) && isDefined(vat) && isNotDefined(total)) {
        return { total: round(net) + round(vat), net, vat };
    }
    if (isDefined(total) && isDefined(net) && isNotDefined(vat)) {
        return { total, net, vat: roundVat(total - net) };
    }
    if (isDefined(total) && isDefined(vat) && isNotDefined(net)) {
        return { total, net: round(total) - round(vat), vat };
    }
    if (isDefined(net) && isNotDefined(vat) && isNotDefined(total)) {
        const derivedVat = roundVat(net * vatRate);
        return { total: round(net) + round(derivedVat), net, vat: derivedVat };
    }
    if (isDefined(total) && isNotDefined(net) && isNotDefined(vat)) {
        const derivedNet = round(total / (1 + vatRate));
        return { total, net: derivedNet, vat: roundVat(total - derivedNet) };
    }
    if (isDefined(vat) && isNotDefined(net) && isNotDefined(total) && vatRate > 0) {
        const derivedNet = round(vat / vatRate);
        return { total: round(derivedNet) + round(vat), net: derivedNet, vat };
    }
    return { total, net, vat };
}

const resolveRossumValue = (options: ResolveRossumValueOptions): number | null => {
    if (options.frozen) {
        return null;
    }
    if (isDefined(options.derived)) {
        return options.derived;
    }
    if (isDefined(options.relatedValue)) {
        return options.targetFieldKind === "unit-price"
            ? options.rounder((options.relatedValue as number) / options.qty)
            : options.rounder((options.relatedValue as number) * options.qty);
    }
    return null;
};
const defaultConfig: RossumRecalculateFieldsConfig = {
    round: (v) => v,
    roundVat: (v) => v,
    sourceFieldKind: "unit-price",
    vatRate: 0,
};

const resolveTriplet = ({
    frozen,
    derived,
    related,
    targetFieldKind,
    qty,
    round,
    roundVat,
}: {
    frozen: { total: boolean; net: boolean; vat: boolean };
    derived: AmountTriplet;
    related: AmountTriplet;
    targetFieldKind: FieldKind;
    qty: number;
    round: (v: number) => number;
    roundVat: (v: number) => number;
}): AmountTriplet => {
    const fields: (keyof AmountTriplet)[] = [
            'total', 'net', 'vat'
    ];
    return Object.fromEntries(
        fields.map((_key) => [
            _key,
            resolveRossumValue({
                frozen: frozen[_key],
                relatedValue: related[_key],
                derived: derived[_key],
                qty,
                rounder: _key === "vat" ? roundVat : round,
                targetFieldKind,
            }),
        ])
    ) as AmountTriplet;
};

const rossumRecalculateFields = (item: ILineItemValues, quantity: number, config = defaultConfig): ILineItemValues => {
    const sourceFieldKind = config.sourceFieldKind;
    const targetFieldKind = sourceFieldKind === "unit-price" ? "amount" : "unit-price";

    const sourceField = sourceFieldKind === "unit-price" ? "TransactionUnitPrice" : "TransactionAmount";
    const targetField = sourceFieldKind === "unit-price" ? "TransactionAmount" : "TransactionUnitPrice";

    const inputTriplet: AmountTriplet = {
        total: rossumPresent(item[sourceField]) ? item[sourceField] : null,
        net: rossumPresent(item[`${sourceField}Net`]) ? item[`${sourceField}Net`] : null,
        vat: rossumPresent(item[`${sourceField}Vat`]) ? item[`${sourceField}Vat`] : null,
    };

    const sourceFrozen = {
        total: isDefined(inputTriplet.total),
        net: isDefined(inputTriplet.net),
        vat: isDefined(inputTriplet.vat),
        selectRecalculateRossumItemStrategy    };
    const completedTriplet = deriveTriplet(inputTriplet, config.vatRate, config.round, config.roundVat);

    const result = { ...item };
    const sharedArgs = { qty: quantity, round: config.round, roundVat: config.roundVat };

    const sourceTriplet = resolveTriplet({
        frozen: sourceFrozen,
        derived: completedTriplet,
        related: NULL_TRIPLET,
        targetFieldKind: sourceFieldKind,
        ...sharedArgs,
    });
    if (isDefined(sourceTriplet.total)) result[sourceField] = sourceTriplet.total;
    if (isDefined(sourceTriplet.net)) result[`${sourceField}Net`] = sourceTriplet.net;
    if (isDefined(sourceTriplet.vat)) result[`${sourceField}Vat`] = sourceTriplet.vat;

    const targetFrozen = {
        total: rossumPresent(item[targetField as keyof ILineItemValues]),
        net: rossumPresent(item[`${targetField}Net`]),
        vat: rossumPresent(item[`${targetField}Vat`]),
    };
    const targetInputTriplet: AmountTriplet = {
        total: rossumPresent(item[targetField as keyof ILineItemValues]) ? item[targetField as keyof ILineItemValues] as number : null,
        net: rossumPresent(item[`${targetField}Net`]) ? item[`${targetField}Net`] : null,
        vat: rossumPresent(item[`${targetField}Vat`]) ? item[`${targetField}Vat`] : null,
    };
    const targetCompletedTriplet = deriveTriplet(targetInputTriplet, config.vatRate, config.round, config.roundVat);
    const sourceResult: AmountTriplet = {
        total: result[sourceField],
        net: result[`${sourceField}Net`],
        vat: result[`${sourceField}Vat`],
    };

    const targetTriplet = resolveTriplet({
        frozen: targetFrozen,
        derived: targetCompletedTriplet,
        related: sourceResult,
        targetFieldKind,
        ...sharedArgs,
    });
    if (isDefined(targetTriplet.total)) result[targetField] = targetTriplet.total;
    if (isDefined(targetTriplet.net)) result[`${targetField}Net`] = targetTriplet.net;
    if (isDefined(targetTriplet.vat)) result[`${targetField}Vat`] = targetTriplet.vat;

    return result;
};

export const recalculateIncompleteRossumItem = (
    item: ILineItemValues,
    decimalPlaces = 2,
    defaultVatRate = 0
): ILineItemValues => {
    const recalcStrategy = selectRecalculateRossumItemStrategy(item, defaultVatRate)

    if (recalcStrategy === null) {
        return item;
    }

    if (recalcStrategy === "zero-triplet") {
        return {
            ...item,
            TransactionAmount: 0,
            TransactionAmountNet: 0,
            TransactionAmountVat: 0,
            TransactionUnitPrice: 0,
            TransactionUnitPriceNet: 0,
            TransactionUnitPriceVat: 0,
            Vat: 0,
        };
    }

    const round = roundToDecimalPlaces.bind(null, decimalPlaces);
    const roundVat = roundVatToDecimalPlaces.bind(null, decimalPlaces);
    const sourceFieldKind = recalcStrategy;
    const sourceField = sourceFieldKind === "unit-price" ? "TransactionUnitPrice" : "TransactionAmount";
    const sourceTriplet: AmountTriplet = {
        total: rossumPresent(item[sourceField]) ? item[sourceField] : null,
        net: rossumPresent(item[`${sourceField}Net`]) ? item[`${sourceField}Net`] : null,
        vat: rossumPresent(item[`${sourceField}Vat`]) ? item[`${sourceField}Vat`] : null,
    };
    const derivedVatRate = rossumPresent(item.Vat) ? item.Vat : deriveVatRateFromTriplet(sourceTriplet, defaultVatRate);
    const vatRate = derivedVatRate / 100;
    const qty = rossumPresent(item.Quantity) ? item.Quantity : 1;

    return {
        ...rossumRecalculateFields(item, qty, {
        round,
        roundVat,
        vatRate,
        sourceFieldKind: recalcStrategy,
        }),
        Vat: derivedVatRate,
    };
};
